import { HttpStatusCode } from "axios";
import type { AnybillResult } from "~/additionalModels/AnybillResponse";
import EndpointPaths from "~/config/constants/EndpointPaths";
import type ActiveDirectoryTokenDto from "~/models/ActiveDirectoryTokenDto";
import type ConfirmPasswordResetDto from "~/models/ConfirmPasswordResetDto";
import type ConfirmPasswordSetupDto from "~/models/ConfirmPasswordSetupDto";
import type LoginUserDto from "~/models/LoginUserDto";
import type MfaResendRequestDto from "~/models/MfaResendRequestDto";
import type MfaVerifyRequestDto from "~/models/MfaVerifyRequestDto";
import type MfaVerifyResponseDto from "~/models/MfaVerifyResponseDto";
import type RequestPasswordResetDto from "~/models/RequestPasswordResetDto";
import type UserInfoDto from "~/models/UserInfoDto";
import BaseService from "~/services/BaseService";

export type LoginOutcome =
  | { outcome: "authenticated" }
  | { outcome: "mfa_required"; challengeId: string }
  | { outcome: "error"; statusCode: number; errorMessage: string | null };

/**
 * Result of setting an initial password from an invitation link. The distinction that matters to
 * the UI is whether the setup token survived the attempt: the backend deliberately releases the
 * link again when only the password was at fault, but consumes it in every other failure case.
 */
export type PasswordSetupOutcome =
  /** Password set, link is spent. The setup flow issues no tokens — the user has to log in. */
  | { outcome: "success" }
  /** The attempt failed but the same token is still usable — keep the form and let the user retry. */
  | { outcome: "retry"; message: string }
  /** The link is expired, already used or otherwise gone — only a fresh link helps. */
  | { outcome: "link_unusable"; message: string }
  /** Transport/server failure with nothing presentable to show — fall back to a generic message. */
  | { outcome: "error" };

export class AuthProvider {
  private static _instance: AuthProvider;

  static get Instance(): AuthProvider {
    if (AuthProvider._instance === undefined)
      AuthProvider._instance = new AuthProvider();

    return AuthProvider._instance;
  }

  private static extractErrorMessage(raw: string | null): string | null {
    if (!raw) return null;
    try {
      JSON.parse(raw);
      return null; // JSON body — fall back to generic i18n message
    }
    catch {
      return raw; // plain string — show as-is
    }
  }

  public async login(loginUserDto: LoginUserDto): Promise<LoginOutcome> {
    const authModule = useAuthModule();
    const loginResponse = await BaseService.Instance.post<ActiveDirectoryTokenDto & { challengeId?: string }>({
      path: EndpointPaths.LOGIN_PATH,
      body: JSON.stringify(loginUserDto),
      authNeeded: false,
    });

    if (!loginResponse.success)
      return { outcome: "error", statusCode: loginResponse.statusCode, errorMessage: AuthProvider.extractErrorMessage(loginResponse.errorMessage) };

    const data = loginResponse.value;

    if (data.challengeId) {
      return { outcome: "mfa_required", challengeId: data.challengeId };
    }

    await authModule.login(data as ActiveDirectoryTokenDto, loginUserDto.username ?? undefined);
    await this.setUser();
    return { outcome: "authenticated" };
  }

  public async verifyMfa(dto: MfaVerifyRequestDto, username: string): Promise<AnybillResult<MfaVerifyResponseDto>> {
    const authModule = useAuthModule();
    const resp = await BaseService.Instance.post<MfaVerifyResponseDto>({
      path: EndpointPaths.MFA_VERIFY_PATH,
      body: JSON.stringify(dto),
      authNeeded: false,
    });

    if (resp.success && resp.value.tokens) {
      const t = resp.value.tokens as any;
      await authModule.login(
        {
          access_token: t.access_token ?? t.accessToken,
          refresh_token: t.refresh_token ?? t.refreshToken,
          tokenType: t.tokenType,
          expiresIn: String(t.expiresIn ?? ""),
        } as ActiveDirectoryTokenDto,
        username,
      );
      await this.setUser();
    }

    return resp;
  }

  public async resendMfa(dto: MfaResendRequestDto): Promise<AnybillResult<{ challengeId: string }>> {
    return BaseService.Instance.post<{ challengeId: string }>({
      path: EndpointPaths.MFA_RESEND_PATH,
      body: JSON.stringify(dto),
      authNeeded: false,
    });
  }

  /**
   * Starts the in-house password reset flow (replaces the old Microsoft-hosted B2C reset page).
   * Always resolves successfully with a challenge id — the backend responds identically whether
   * or not the username is known, so the frontend never learns which emails have an account.
   */
  public async requestPasswordReset(dto: RequestPasswordResetDto): Promise<AnybillResult<{ challengeId: string; message: string }>> {
    return BaseService.Instance.post<{ challengeId: string; message: string }>({
      path: EndpointPaths.REQUEST_PASSWORD_RESET_PATH,
      body: JSON.stringify(dto),
      authNeeded: false,
    });
  }

  /**
   * Verifies the password reset code and sets the new password via the backend (Graph API under the hood).
   */
  public async confirmPasswordReset(dto: ConfirmPasswordResetDto): Promise<AnybillResult<void>> {
    return BaseService.Instance.post<void>({
      path: EndpointPaths.CONFIRM_PASSWORD_RESET_PATH,
      body: JSON.stringify(dto),
      authNeeded: false,
    });
  }

  /**
   * Sets the initial password for a freshly invited portal user. Anonymous endpoint — the user has
   * no credentials yet, so no `Authorization` header is sent.
   *
   * The setup token is a credential: it is forwarded verbatim and never logged, echoed or attached
   * to any telemetry payload here.
   *
   * Failures come back as German plain-text messages that are passed through to the user unchanged,
   * so backend wording changes can't silently drift from what the portal shows. Only the outcome
   * classification looks at the text, and only at the fragments that decide whether the link
   * survived the attempt.
   */
  public async confirmPasswordSetup(dto: ConfirmPasswordSetupDto): Promise<PasswordSetupOutcome> {
    const resp = await BaseService.Instance.post<void>({
      path: EndpointPaths.CONFIRM_PASSWORD_SETUP_PATH,
      body: JSON.stringify(dto),
      authNeeded: false,
    });

    if (resp.success)
      return { outcome: "success" };

    const message = resp.errorMessage?.trim();

    // Only a 400 carries a meaningful, user-presentable reason. Anything else (network, 5xx, an
    // empty body) has nothing worth showing, and guessing whether the link survived would be worse
    // than letting the user try again.
    if (resp.statusCode !== HttpStatusCode.BadRequest || !message)
      return { outcome: "error" };

    // "Das neue Passwort erfüllt nicht die Anforderungen. …" — the directory rejected the password
    // itself, the backend released the link again, so the very same token can be reused.
    if (message.includes("erfüllt nicht die Anforderungen"))
      return { outcome: "retry", message };

    // "… ist ungültig oder abgelaufen. …" / "Das Passwort konnte nicht gesetzt werden. …" — the
    // link is gone either way (in the latter case the write may even have landed).
    if (message.includes("ungültig oder abgelaufen") || message.includes("konnte nicht gesetzt werden"))
      return { outcome: "link_unusable", message };

    // Unknown 400 — most likely DTO validation (the API answers those with a
    // ValidationProblemDetails object rather than a bare string). Nothing says the link was
    // consumed, so show what the backend said and keep the form usable.
    return { outcome: "retry", message };
  }

  public async refreshToken(refreshToken: string): Promise<AnybillResult<ActiveDirectoryTokenDto>> {
    return BaseService.Instance.post<ActiveDirectoryTokenDto>({
      path: EndpointPaths.REFRESH_TOKEN_PATH,
      body: refreshToken,
      authNeeded: false,
    });
  }

  public async setUser(): Promise<AnybillResult<UserInfoDto>> {
    const authModule = useAuthModule();
    const userInfoResponse = await BaseService.Instance.get<UserInfoDto>({
      path: EndpointPaths.GET_USER_INFO_PATH,
    });

    if (userInfoResponse.success) {
      const isVend = userInfoResponse.value.type === "vendorCustomer";
      const isDist = userInfoResponse.value.type === "distributorCustomer";
      await dispatchModuleLoad(isVend, isDist, userInfoResponse.value);
    }
    else if (userInfoResponse.statusCode === HttpStatusCode.Forbidden) {
      authModule.logout();
    }

    return userInfoResponse;
  }
}
